import type { JSX, SVGProps } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useGetEconInfoQuery, useListEconRunsQuery } from '@shared/api';
import { Card, CardTitle, CardSubtitle } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { IconArrowRight } from '@shared/ui/icons';
import { blockDrop } from '@shared/theme/animations';
import { ECON_MODULES, isEconModuleId, type EconModuleId } from '@entities/economic/types';
import { EconDashboard } from './EconDashboard';

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

/** Карточка модуля: blockDrop с per-card задержкой — «блоки укладываются». */
const ModuleCard = styled(Card)<{ $index: number }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  animation: ${blockDrop} 480ms ${({ theme }) => theme.motion.spring} both;
  animation-delay: ${({ $index }) => `${$index * 90}ms`};
`;

const IconBadge = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Description = styled.div`
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 1;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: ${({ theme }) => theme.spacing(2)};

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }

  ${CardLink}:hover & > svg {
    transform: translateX(4px);
  }
`;

const DashboardBlock = styled.div`
  margin-top: ${({ theme }) => theme.spacing(8)};
`;

const Note = styled.div`
  padding: ${({ theme }) => theme.spacing(4)} 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function ModuleSvg({ size = 22, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

/** БДР — столбиковая диаграмма (trending). */
function IconBudget(props: IconProps) {
  return (
    <ModuleSvg {...props}>
      <path d="M3 3v18h18" />
      <rect x="7" y="11" width="3" height="6" rx="0.5" />
      <rect x="12" y="7" width="3" height="10" rx="0.5" />
      <rect x="17" y="13" width="3" height="4" rx="0.5" />
    </ModuleSvg>
  );
}

/** Добыча — кирка. */
function IconPick(props: IconProps) {
  return (
    <ModuleSvg {...props}>
      <path d="M14.5 9.5 21 3" />
      <path d="M3 21c2-5 5.5-8.5 10.5-10.5" />
      <path d="M5 5c4 0 9 2.5 11.5 6.5" />
      <path d="M14 4c2 1 4 3 5 5" />
    </ModuleSvg>
  );
}

/** Столовая — приборы. */
function IconUtensils(props: IconProps) {
  return (
    <ModuleSvg {...props}>
      <path d="M7 3v18" />
      <path d="M4 3v6a3 3 0 0 0 6 0V3" />
      <path d="M17 3c-2 0-3 2-3 5s1 4 3 4" />
      <path d="M17 3v18" />
    </ModuleSvg>
  );
}

/** БДДС — банкноты/поток денег. */
function IconCashflow(props: IconProps) {
  return (
    <ModuleSvg {...props}>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01M18 12h.01" />
    </ModuleSvg>
  );
}

/** Пакет руководству — портфель/сводка (агрегат по всем направлениям). */
function IconExecPack(props: IconProps) {
  return (
    <ModuleSvg {...props}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M3 12h18" />
    </ModuleSvg>
  );
}

const MODULE_ICON: Record<EconModuleId, (p: IconProps) => JSX.Element> = {
  bdr: IconBudget,
  mining: IconPick,
  canteen: IconUtensils,
  cashflow: IconCashflow,
  exec_pack: IconExecPack,
};

export function EconPage() {
  const { data, isLoading, isError } = useGetEconInfoQuery();
  // Лёгкий запрос для дашборда — без polling: страница-витрина, не монитор.
  const { data: runsData } = useListEconRunsQuery({});

  // Источник истины по составу/доступности — backend (`info.modules`);
  // визуальное обогащение (иконка, расширенное описание) берём из локального
  // конфига по id, c аккуратным фолбэком на присланные поля.
  const modules = data?.modules ?? [];

  return (
    <div>
      <SectionLabel>Модули агента</SectionLabel>

      {isLoading && <Note>Загрузка модулей…</Note>}
      {isError && <Note>Не удалось загрузить описание агента. Обновите страницу.</Note>}

      {modules.length > 0 && (
        <Grid>
          {modules.map((m, i) => {
            // Сужаем m.id (string из API) до известного union в одном выражении —
            // тогда индексация Record<EconModuleId, …> типобезопасна.
            const cfg = isEconModuleId(m.id) ? ECON_MODULES[m.id] : null;
            const Icon = isEconModuleId(m.id) ? MODULE_ICON[m.id] : null;
            const title = cfg?.title ?? m.title;
            const description = cfg?.description ?? m.description;
            const to = `/agents/econ/runs?module=${encodeURIComponent(m.id)}`;
            return (
              <CardLink key={m.id} to={to} aria-label={`Открыть модуль «${title}»`}>
                <ModuleCard $interactive $accent $index={i}>
                  {Icon && (
                    <IconBadge>
                      <Icon />
                    </IconBadge>
                  )}
                  <TitleRow>
                    <CardTitle as="span">{title}</CardTitle>
                    <Badge $tone="success">{cfg?.short ?? 'модуль'}</Badge>
                  </TitleRow>
                  <CardSubtitle as="div">
                    {cfg?.headlineMetric ?? 'Сверка план / факт'}
                  </CardSubtitle>
                  <Description>{description}</Description>
                  <CardFooter>
                    Открыть обработки
                    <IconArrowRight size={16} />
                  </CardFooter>
                </ModuleCard>
              </CardLink>
            );
          })}
        </Grid>
      )}

      <DashboardBlock>
        <SectionLabel>Сводка</SectionLabel>
        <EconDashboard runs={runsData?.items ?? []} />
      </DashboardBlock>
    </div>
  );
}
